<template>
  <div>
    <div v-if="loading" class="flex justify-center py-16">
      <div class="animate-spin rounded-full h-10 w-10 border-t-4 border-b-4 border-yellow-500"></div>
    </div>

    <div v-else>
      <!-- No documents yet — message + Upload button (no header row to dock it in) -->
      <div v-if="documents.length === 0" class="flex items-center justify-between mb-4 pb-3 border-b">
        <p class="text-gray-400 text-sm">No documents have been uploaded for this patient.</p>
        <button
          @click="showUploadArea = !showUploadArea"
          class="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-custom-yellow text-white rounded-md hover:bg-orange-500 transition shrink-0"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"/>
          </svg>
          {{ showUploadArea ? 'Cancel' : 'Upload Document' }}
        </button>
      </div>

      <!-- Inline upload area -->
      <div v-if="showUploadArea" class="mb-6 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-5">
        <p class="text-sm font-medium text-gray-700 mb-3">Select a file (Images or PDF, max 5 MB)</p>

        <div
          class="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors"
          :class="isDragging ? 'border-custom-yellow bg-yellow-50' : 'border-gray-300 hover:border-custom-yellow hover:bg-gray-100'"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="onDrop"
          @click="$refs.docFileInput.click()"
        >
          <p class="text-sm text-gray-600">
            <span class="font-medium text-custom-yellow">Click to browse</span> or drag and drop
          </p>
          <p class="text-xs text-gray-400 mt-1">PNG, JPG, PDF — up to 5 MB</p>
          <input
            ref="docFileInput"
            type="file"
            accept="image/*,.pdf"
            class="hidden"
            @change="onFileSelected"
          />
        </div>

        <div v-if="pendingFile" class="mt-4 flex items-center gap-4 p-3 bg-white rounded-lg border border-gray-200">
          <img
            v-if="pendingPreviewUrl"
            :src="pendingPreviewUrl"
            class="h-14 w-14 object-cover rounded flex-shrink-0"
            alt="preview"
          />
          <div v-else class="h-14 w-14 flex items-center justify-center bg-red-50 rounded flex-shrink-0">
            <span class="text-xs font-bold text-red-600">PDF</span>
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-gray-700 truncate">{{ pendingFile.name }}</p>
          </div>
          <button
            @click="uploadDocument"
            :disabled="isUploading"
            class="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium bg-custom-yellow text-white rounded-md hover:bg-orange-500 disabled:opacity-50"
          >
            {{ isUploading ? 'Uploading…' : 'Upload' }}
          </button>
          <button @click="clearPendingFile" class="p-1.5 text-gray-400 hover:text-gray-600">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <p v-if="uploadError" class="mt-2 text-sm text-red-500">{{ uploadError }}</p>
      </div>

      <template v-if="documents.length">
      <!-- Header: counter + filename + prev/next -->
      <div class="flex items-center justify-between mb-4 pb-3 border-b">
        <div class="min-w-0">
          <p class="text-xs text-gray-400">Document {{ currentIndex + 1 }} of {{ documents.length }}</p>
          <p class="text-sm font-medium text-gray-700 truncate" :title="currentDoc.FileName">
            {{ currentDoc.FileName || "Document" }}
          </p>
        </div>
        <div class="flex items-center gap-2 shrink-0">
          <button
            @click="showUploadArea = !showUploadArea"
            class="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium bg-custom-yellow text-white rounded-md hover:bg-orange-500 transition"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"/>
            </svg>
            {{ showUploadArea ? 'Cancel' : 'Upload Document' }}
          </button>
          <button
            @click="previous"
            :disabled="currentIndex === 0"
            class="px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ← Previous
          </button>
          <button
            @click="next"
            :disabled="currentIndex === documents.length - 1"
            class="px-3 py-1.5 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next →
          </button>
        </div>
      </div>

      <!-- Document viewer -->
      <div class="flex items-center justify-center bg-gray-50 rounded-lg p-4" style="min-height: 50vh;">
        <img
          v-if="currentDoc.FileType && currentDoc.FileType.startsWith('image/')"
          :src="currentDoc.FileURL"
          :alt="currentDoc.FileName"
          class="max-w-full object-contain rounded shadow"
          style="max-height: 55vh;"
        />
        <iframe
          v-else-if="currentDoc.FileType === 'application/pdf'"
          :src="currentDoc.FileURL"
          class="w-full rounded border-0"
          style="height: 55vh;"
          title="Document preview"
        />
        <div v-else class="flex flex-col items-center text-gray-500 py-16">
          <svg class="h-16 w-16 mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p class="text-sm font-medium text-gray-600 mb-1">{{ currentDoc.FileName }}</p>
          <p class="text-xs text-gray-400 mb-5">This file type cannot be previewed in the browser.</p>
          <a
            :href="currentDoc.FileURL"
            download
            class="px-4 py-2 text-sm font-medium bg-custom-yellow text-white rounded-md hover:bg-orange-500 transition"
          >
            Download File
          </a>
        </div>
      </div>

      <!-- Thumbnail strip for quick jump -->
      <div v-if="documents.length > 1" class="flex gap-2 mt-4 overflow-x-auto pb-1">
        <button
          v-for="(doc, index) in documents"
          :key="doc.DocumentId"
          @click="currentIndex = index"
          class="shrink-0 w-14 h-14 rounded border-2 overflow-hidden"
          :class="index === currentIndex ? 'border-custom-yellow' : 'border-gray-200'"
        >
          <img
            v-if="doc.FileType && doc.FileType.startsWith('image/')"
            :src="doc.FileURL"
            class="w-full h-full object-cover"
            :alt="doc.FileName"
          />
          <div v-else class="w-full h-full flex items-center justify-center bg-gray-100 text-red-400 text-xs font-semibold">
            PDF
          </div>
        </button>
      </div>
      </template>
    </div>
  </div>
</template>

<script>
import apiClient from "@/services/api";
import AWS from "aws-sdk";

export default {
  name: "PatientDocumentsTab",
  props: {
    patientId: { type: String, default: null },
  },
  data() {
    return {
      documents: [],
      loading: false,
      currentIndex: 0,
      showUploadArea: false,
      isDragging: false,
      pendingFile: null,
      pendingPreviewUrl: null,
      isUploading: false,
      uploadError: null,
    };
  },
  computed: {
    currentDoc() {
      return this.documents[this.currentIndex] || {};
    },
  },
  watch: {
    patientId: {
      immediate: true,
      handler(val) {
        if (val) this.fetchDocuments();
      },
    },
  },
  methods: {
    async fetchDocuments() {
      this.loading = true;
      this.currentIndex = 0;
      try {
        const response = await apiClient.getPatient(this.patientId);
        this.documents = response.data?.uploaded_documents || [];
      } catch (error) {
        console.error("Failed to fetch patient documents:", error);
        this.documents = [];
      } finally {
        this.loading = false;
      }
    },
    next() {
      if (this.currentIndex < this.documents.length - 1) this.currentIndex++;
    },
    previous() {
      if (this.currentIndex > 0) this.currentIndex--;
    },

    onDrop(event) {
      this.isDragging = false;
      const file = event.dataTransfer?.files?.[0];
      if (file) this.setPendingFile(file);
    },
    onFileSelected(event) {
      const file = event.target.files?.[0];
      if (file) this.setPendingFile(file);
    },
    setPendingFile(file) {
      this.uploadError = null;
      const MAX = 5 * 1024 * 1024;
      const ALLOWED = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'application/pdf'];
      if (!ALLOWED.includes(file.type)) {
        this.uploadError = 'Invalid file type. Please upload an image or PDF.';
        return;
      }
      if (file.size > MAX) {
        this.uploadError = 'File is too large. Maximum allowed size is 5 MB.';
        return;
      }
      if (this.pendingPreviewUrl) URL.revokeObjectURL(this.pendingPreviewUrl);
      this.pendingFile = file;
      this.pendingPreviewUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
    },
    clearPendingFile() {
      if (this.pendingPreviewUrl) URL.revokeObjectURL(this.pendingPreviewUrl);
      this.pendingFile = null;
      this.pendingPreviewUrl = null;
      this.uploadError = null;
    },

    async uploadDocument() {
      if (!this.pendingFile) return;
      this.isUploading = true;
      this.uploadError = null;
      try {
        const s3 = new AWS.S3({
          endpoint:         process.env.VUE_APP_DO_ENDPOINT,
          signatureVersion: 'v4',
          accessKeyId:      process.env.VUE_APP_DO_ACCESS_KEY,
          secretAccessKey:  process.env.VUE_APP_DO_SECRET_KEY,
          region:           process.env.VUE_APP_DO_REGION,
        });

        const key = `patient-documents/${this.patientId}-${Date.now()}-${this.pendingFile.name}`;
        const result = await s3.upload({
          Bucket:      'jiji-bucket',
          Key:         key,
          Body:        this.pendingFile,
          ACL:         'public-read',
          ContentType: this.pendingFile.type,
        }).promise();

        const response = await apiClient.addPatientDocument(this.patientId, {
          fileName: this.pendingFile.name,
          fileType: this.pendingFile.type,
          fileUrl:  result.Location,
        });

        if (response.success) {
          this.documents.push({
            DocumentId: response.data?.DocumentId ?? `pending-${Date.now()}`,
            FileName:   this.pendingFile.name,
            FileType:   this.pendingFile.type,
            FileURL:    result.Location,
          });
          this.currentIndex = this.documents.length - 1;
          this.$toast.success('Document uploaded successfully.');
          this.clearPendingFile();
          this.showUploadArea = false;
        } else {
          this.uploadError = response.message || 'Failed to save document. Please try again.';
        }
      } catch (err) {
        console.error('uploadDocument error:', err);
        this.uploadError = 'Upload failed. Please check your connection and try again.';
      } finally {
        this.isUploading = false;
      }
    },
  },
};
</script>
